import { Suspense } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import ScrollToTop from "./components/ScrollToTop";
import PageTracker from "./components/PageTracker";
import RedirectsResolver from "./components/RedirectsResolver";
import { ThemeProvider } from "./components/theme/ThemeProvider";
import ExitIntentPopup from "./components/site/ExitIntentPopup";

// Static: hotfix — eager-load the two primary public routes
import IndexV2 from "./pages/IndexV2.tsx";
import InfrastructureLayer from "./pages/Platform.tsx";

// Lazy: remaining public routes (per-route code split, P0d)
const Insights = lazy(() => import("./pages/Insights.tsx"));
const Events = lazy(() => import("./pages/Events.tsx"));
const InsightsCategory = lazy(() => import("./pages/InsightsCategory.tsx"));
const InsightDetail = lazy(() => import("./pages/InsightDetail.tsx"));
const Company = lazy(() => import("./pages/Company.tsx"));
const Careers = lazy(() => import("./pages/Careers.tsx"));
const Legal = lazy(() => import("./pages/Legal.tsx"));
const PublishedPage = lazy(() => import("./pages/PublishedPage.tsx"));
const WorkflowDetail = lazy(() => import("./pages/WorkflowDetail.tsx"));
const ReleaseNotes = lazy(() => import("./pages/ReleaseNotes.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const FAQ = lazy(() => import("./pages/FAQ.tsx"));
const InstructionsForUse = lazy(() => import("./pages/InstructionsForUse.tsx"));
const AnalyticsOptOut = lazy(() => import("./pages/AnalyticsOptOut.tsx"));

const SlideBotWizard = lazy(() => import("./pages/SlideBotWizard.tsx"));
const WhitepaperDev = lazy(() => import("./pages/WhitepaperDev.tsx"));
const ClinicalAIBottleneck = lazy(() => import("./pages/ClinicalAIBottleneck.tsx"));
const InfrastructureEraOfClinicalAI = lazy(() => import("./pages/InfrastructureEraOfClinicalAI.tsx"));
const BeckersRoundtable = lazy(() => import("./pages/BeckersRoundtable.tsx"));

// Design Hub (gated to @deepc.ai)
const RequireDeepcAiEmail = lazy(() => import("./components/tools/RequireDeepcAiEmail.tsx"));
const DesignHub = lazy(() => import("./pages/design-hub/DesignHub.tsx"));
const DesignHubImage = lazy(() => import("./pages/design-hub/ImageTool.tsx"));
const DesignHubPresentation = lazy(() => import("./pages/design-hub/PresentationTool.tsx"));
const DesignHubDocuments = lazy(() => import("./pages/design-hub/DocumentsTool.tsx"));
const DesignHubInfographic = lazy(() => import("./pages/design-hub/InfographicTool.tsx"));
const DesignHubProductUi = lazy(() => import("./pages/design-hub/ProductUiTool.tsx"));
const DesignHubBrandAssets = lazy(() => import("./pages/design-hub/BrandAssetsTool.tsx"));
const DesignHubBackgrounds = lazy(() => import("./pages/design-hub/BackgroundsTool.tsx"));
const DesignHubBgRooms = lazy(() => import("./pages/design-hub/BackgroundRoomsTool.tsx"));
const DesignHubBgLinkedIn = lazy(() => import("./pages/design-hub/BackgroundLinkedInTool.tsx"));
const DesignHubNameCard = lazy(() => import("./pages/design-hub/NameCardTool.tsx"));
const AdminDesignHubUsage = lazy(() => import("./pages/admin/DesignHubUsage.tsx"));


// Lazy: admin + presentations (kept out of public entry chunk)
const RequireRole = lazy(() => import("./components/admin/RequireRole"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AnalyticsShell = lazy(() => import("./components/admin/AnalyticsShell.tsx"));
const ContactCardPage = lazy(() => import("./pages/ContactCard.tsx"));
const AdminContactCards = lazy(() => import("./pages/admin/ContactCards.tsx"));
const AdminRedirects = lazy(() => import("./pages/admin/RedirectsList.tsx"));
const AdminDesignSystem = lazy(() => import("./pages/admin/DesignSystem.tsx"));
const AdminLogin = lazy(() => import("./pages/admin/Login.tsx"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard.tsx"));
const AdminInsightsList = lazy(() => import("./pages/admin/InsightsList.tsx"));
const AdminInsightEditor = lazy(() => import("./pages/admin/InsightEditor.tsx"));
const AdminInsightsImport = lazy(() => import("./pages/admin/InsightsImport.tsx"));
const AdminWorkflowsList = lazy(() => import("./pages/admin/WorkflowsList.tsx"));
const AdminWorkflowEditor = lazy(() => import("./pages/admin/WorkflowEditor.tsx"));
const AdminPagesList = lazy(() => import("./pages/admin/PagesList.tsx"));
const AdminLandingPagesList = lazy(() => import("./pages/admin/LandingPagesList.tsx"));
const AdminPageEditor = lazy(() => import("./pages/admin/PageEditor.tsx"));
const AdminPagePreview = lazy(() => import("./pages/admin/PagePreview.tsx"));
const AdminInsightPreview = lazy(() => import("./pages/admin/InsightPreview.tsx"));
const AdminBannerEditor = lazy(() => import("./pages/admin/BannerEditor.tsx"));
const AdminHeroVideo = lazy(() => import("./pages/admin/HeroVideoAdmin.tsx"));
const AdminPopupsList = lazy(() => import("./pages/admin/PopupsList.tsx"));
const AdminPopupEditor = lazy(() => import("./pages/admin/PopupEditor.tsx"));
const AdminUsers = lazy(() => import("./pages/admin/UsersAdmin.tsx"));
const CRMLayout = lazy(() => import("./pages/admin/crm/CRMLayout.tsx"));
const CRMContacts = lazy(() => import("./pages/admin/crm/ContactsList.tsx"));
const CRMContactDetail = lazy(() => import("./pages/admin/crm/ContactDetail.tsx"));
const CRMAccounts = lazy(() => import("./pages/admin/crm/AccountsList.tsx"));
const CRMAccountDetail = lazy(() => import("./pages/admin/crm/AccountDetail.tsx"));
const CRMTriage = lazy(() => import("./pages/admin/crm/TriageInbox.tsx"));
const CRMRules = lazy(() => import("./pages/admin/crm/TriageRules.tsx"));
const CRMAssets = lazy(() => import("./pages/admin/crm/Assets.tsx"));
const CRMSubmissions = lazy(() => import("./pages/admin/crm/Submissions.tsx"));
const CRMSync = lazy(() => import("./pages/admin/crm/SyncSettings.tsx"));
const CRMAudit = lazy(() => import("./pages/admin/crm/AuditLog.tsx"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics.tsx"));
const AdminAnalyticsExplorer = lazy(() => import("./pages/admin/AnalyticsExplorer.tsx"));
const AdminAnalyticsRetention = lazy(() => import("./pages/admin/AnalyticsRetention.tsx"));
const AdminAnalyticsConversions = lazy(() => import("./pages/admin/AnalyticsConversions.tsx"));
const AdminReleaseNotes = lazy(() => import("./pages/admin/ReleaseNotesList.tsx"));
const ImageGeneratorTool = lazy(() => import("./pages/tools/ImageGenerator.tsx"));
const WhitepaperTool = lazy(() => import("./pages/tools/WhitepaperTool.tsx"));
const WhitepaperToolEditor = lazy(() => import("./pages/tools/WhitepaperToolEditor.tsx"));
const AdminQuotes = lazy(() => import("./pages/admin/quotes/QuotesList.tsx"));
const AdminGuide = lazy(() => import("./pages/admin/guide/GuideList.tsx"));
const AdminCampaignsList = lazy(() => import("./pages/admin/campaigns/CampaignsList.tsx"));
const AdminNewCampaign = lazy(() => import("./pages/admin/campaigns/NewCampaign.tsx"));
const AdminCampaignLayout = lazy(() => import("./pages/admin/campaigns/CampaignLayout.tsx"));
const AdminCampaignBrief = lazy(() => import("./pages/admin/campaigns/CampaignBrief.tsx"));
const AdminCampaignResearch = lazy(() => import("./pages/admin/campaigns/CampaignResearch.tsx"));
const AdminCampaignPlan = lazy(() => import("./pages/admin/campaigns/CampaignPlan.tsx"));
const AdminCampaignStory = lazy(() => import("./pages/admin/campaigns/CampaignStory.tsx"));
const AdminCampaignPageTab = lazy(() => import("./pages/admin/campaigns/CampaignPage.tsx"));
const AdminCampaignKitTab = lazy(() => import("./pages/admin/campaigns/CampaignKit.tsx"));
const AdminCampaignGallery = lazy(() => import("./pages/admin/CampaignGallery.tsx"));
const PresentationGate = lazy(() => import("./pages/presentations/PresentationGate.tsx"));
const AdminPresentationsList = lazy(() => import("./pages/presentations/AdminPresentationsList.tsx"));
const AdminPresentationEditor = lazy(() => import("./pages/presentations/AdminPresentationEditor.tsx"));


const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <PageTracker />
        <RedirectsResolver />
        <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<IndexV2 />} />
          <Route path="/solution" element={<InfrastructureLayer />} />
          <Route path="/lp/clinical-ai-bottleneck" element={<ClinicalAIBottleneck />} />
          <Route path="/clinical-ai-bottleneck" element={<Navigate to="/lp/clinical-ai-bottleneck" replace />} />
          <Route path="/lp/infrastructure-era-of-clinical-ai" element={<InfrastructureEraOfClinicalAI />} />
          <Route path="/c/:code" element={<ContactCardPage />} />
          <Route path="/lp/beckers-2026" element={<BeckersRoundtable />} />
          <Route path="/beckers-2026" element={<Navigate to="/lp/beckers-2026" replace />} />
          
          <Route path="/insights" element={<Insights />} />
          <Route path="/events" element={<Events />} />

          <Route path="/insights/faq" element={<Navigate to="/faq" replace />} />
          <Route path="/insights/leadership-faq" element={<Navigate to="/faq" replace />} />
          <Route path="/insights/workflows" element={<Navigate to="/insights" replace />} />
          <Route path="/insights/testimonials" element={<Navigate to="/insights" replace />} />
          <Route path="/insights/workflows/:slug" element={<WorkflowDetail />} />
          <Route path="/event/:slug" element={<InsightDetail namespace="event" />} />
          <Route path="/insight/:slug" element={<InsightDetail namespace="insight" />} />
          <Route path="/insights/:slug" element={<InsightsCategory />} />
          <Route path="/company" element={<Company />} />
          <Route path="/company/careers" element={<Careers />} />
          
          <Route path="/contact" element={<Navigate to="/form/contact" replace />} />
          <Route path="/legal/:slug" element={<Legal />} />
          <Route path="/form/:slug" element={<PublishedPage template="form" />} />
          <Route path="/submission/:slug" element={<PublishedPage template="thank_you" />} />
          <Route path="/p/:slug" element={<PublishedPage template="custom" />} />
          <Route path="/release-notes" element={<ReleaseNotes />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/faqs" element={<Navigate to="/faq" replace />} />
          <Route path="/instructions-for-use" element={<InstructionsForUse />} />
          <Route path="/newsletter" element={<Navigate to="/form/newsletter" replace />} />
          <Route path="/analytics-opt-out" element={<AnalyticsOptOut />} />
          <Route path="/tools/slide-bot" element={<SlideBotWizard />} />
          <Route path="/slide-bot" element={<Navigate to="/tools/slide-bot" replace />} />
          <Route path="/slide-bot-wizard" element={<Navigate to="/tools/slide-bot" replace />} />

          <Route path="/tools/image-generator" element={<ImageGeneratorTool />} />
          <Route path="/admin/image-generator" element={<Navigate to="/tools/image-generator" replace />} />

          <Route path="/tools/whitepaper" element={<WhitepaperTool />} />
          <Route path="/tools/whitepaper/:id" element={<WhitepaperToolEditor />} />

          {/* Design Hub */}
          <Route
            path="/design-hub"
            element={
              <RequireDeepcAiEmail toolName="the Design hub" title="Design hub" subtitle="Make something on brand, without waiting for a designer.">
                <DesignHub />
              </RequireDeepcAiEmail>
            }
          />
          <Route
            path="/design-hub/image"
            element={
              <RequireDeepcAiEmail toolName="the Design hub" title="Design hub" subtitle="Make something on brand, without waiting for a designer.">
                <DesignHubImage />
              </RequireDeepcAiEmail>
            }
          />
          <Route
            path="/design-hub/presentation"
            element={
              <RequireDeepcAiEmail toolName="the Design hub" title="Design hub" subtitle="Make something on brand, without waiting for a designer.">
                <DesignHubPresentation />
              </RequireDeepcAiEmail>
            }
          />
          <Route
            path="/design-hub/documents"
            element={
              <RequireDeepcAiEmail toolName="the Design hub" title="Design hub" subtitle="Make something on brand, without waiting for a designer.">
                <DesignHubDocuments />
              </RequireDeepcAiEmail>
            }
          />
          <Route
            path="/design-hub/infographic"
            element={
              <RequireDeepcAiEmail toolName="the Design hub" title="Design hub" subtitle="Make something on brand, without waiting for a designer.">
                <DesignHubInfographic />
              </RequireDeepcAiEmail>
            }
          />
          <Route
            path="/design-hub/product-ui"
            element={
              <RequireDeepcAiEmail toolName="the Design hub" title="Design hub" subtitle="Make something on brand, without waiting for a designer.">
                <DesignHubProductUi />
              </RequireDeepcAiEmail>
            }
          />
          <Route
            path="/design-hub/brand-assets"
            element={
              <RequireDeepcAiEmail toolName="the Design hub" title="Design hub" subtitle="Make something on brand, without waiting for a designer.">
                <DesignHubBrandAssets />
              </RequireDeepcAiEmail>
            }
          />

          <Route
            path="/design-hub/backgrounds"
            element={
              <RequireDeepcAiEmail toolName="the Design hub" title="Design hub" subtitle="Make something on brand, without waiting for a designer.">
                <DesignHubBackgrounds />
              </RequireDeepcAiEmail>
            }
          />
          <Route
            path="/design-hub/backgrounds/rooms"
            element={
              <RequireDeepcAiEmail toolName="the Design hub" title="Design hub" subtitle="Make something on brand, without waiting for a designer.">
                <DesignHubBgRooms />
              </RequireDeepcAiEmail>
            }
          />
          <Route
            path="/design-hub/backgrounds/linkedin"
            element={
              <RequireDeepcAiEmail toolName="the Design hub" title="Design hub" subtitle="Make something on brand, without waiting for a designer.">
                <DesignHubBgLinkedIn />
              </RequireDeepcAiEmail>
            }
          />
          <Route
            path="/design-hub/backgrounds/name-card"
            element={
              <RequireDeepcAiEmail toolName="the Design hub" title="Design hub" subtitle="Make something on brand, without waiting for a designer.">
                <DesignHubNameCard />
              </RequireDeepcAiEmail>
            }
          />

          {/* Presentations (gated, isolated) */}
          <Route path="/present/:token" element={<PresentationGate />} />

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin/pages/:id/preview"
            element={
              <RequireRole>
                <AdminPagePreview />
              </RequireRole>
            }
          />
          <Route
            path="/admin/insights/:id/preview"
            element={
              <RequireRole>
                <AdminInsightPreview />
              </RequireRole>
            }
          />
          <Route
            path="/admin/campaign-gallery"
            element={
              <RequireRole>
                <AdminCampaignGallery />
              </RequireRole>
            }
          />
          <Route
            path="/admin/campaign-gallery/:section"
            element={
              <RequireRole>
                <AdminCampaignGallery />
              </RequireRole>
            }
          />

          <Route
            path="/admin"
            element={
              <RequireRole>
                <AdminLayout />
              </RequireRole>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="insights" element={<AdminInsightsList />} />
            <Route path="insights/import" element={<AdminInsightsImport />} />
            <Route path="insights/:id" element={<AdminInsightEditor />} />
            <Route path="workflows" element={<AdminWorkflowsList />} />
            <Route path="workflows/:id" element={<AdminWorkflowEditor />} />
            <Route path="pages" element={<AdminPagesList />} />
            <Route path="pages/:id" element={<AdminPageEditor />} />
            <Route path="landing-pages" element={<AdminLandingPagesList />} />
            <Route path="banners" element={<AdminBannerEditor />} />
            <Route path="home-video" element={<AdminHeroVideo />} />
            <Route path="banners/:id" element={<AdminBannerEditor />} />


            <Route path="popups" element={<AdminPopupsList />} />
            <Route path="popups/:id" element={<AdminPopupEditor />} />
            <Route path="analytics" element={<AnalyticsShell />}>
              <Route index element={<AdminAnalytics />} />
              <Route path="explorer" element={<AdminAnalyticsExplorer />} />
              <Route path="retention" element={<AdminAnalyticsRetention />} />
              <Route path="conversions" element={<AdminAnalyticsConversions />} />
            </Route>
            <Route
              path="design-hub-usage"
              element={
                <RequireRole role="admin">
                  <AdminDesignHubUsage />
                </RequireRole>
              }
            />

            <Route path="design-system" element={<AdminDesignSystem />} />

            <Route path="release-notes" element={<AdminReleaseNotes />} />
            <Route path="redirects" element={<AdminRedirects />} />
            <Route path="contact-cards" element={<AdminContactCards />} />
            <Route path="presentations" element={<AdminPresentationsList />} />
            <Route path="presentations/:id" element={<AdminPresentationEditor />} />
            <Route
              path="users"
              element={
                <RequireRole role="admin">
                  <AdminUsers />
                </RequireRole>
              }
            />
            <Route path="crm" element={<CRMLayout />}>
              <Route index element={<CRMContacts />} />
              <Route path="accounts" element={<CRMAccounts />} />
              <Route path="accounts/:id" element={<CRMAccountDetail />} />
              <Route path="triage" element={<CRMTriage />} />
              <Route path="rules" element={<CRMRules />} />
              <Route path="assets" element={<CRMAssets />} />
              <Route path="submissions" element={<CRMSubmissions />} />
              <Route path="sync" element={<CRMSync />} />
              <Route path="audit" element={<CRMAudit />} />
              <Route path=":id" element={<CRMContactDetail />} />
            </Route>
            <Route path="quotes" element={<AdminQuotes />} />
            <Route path="guide" element={<AdminGuide />} />
            <Route path="campaigns" element={<AdminCampaignsList />} />
            <Route path="campaigns/new" element={<AdminNewCampaign />} />
            <Route path="campaigns/:id" element={<AdminCampaignLayout />}>
              <Route index element={<AdminCampaignBrief />} />
              <Route path="research" element={<AdminCampaignResearch />} />
              <Route path="plan" element={<AdminCampaignPlan />} />
              <Route path="story" element={<AdminCampaignStory />} />
              <Route path="page" element={<AdminCampaignPageTab />} />
              <Route path="kit" element={<AdminCampaignKitTab />} />
            </Route>
          </Route>

          <Route path="/whitepaper-dev" element={<WhitepaperDev />} />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />

        </Routes>
        </Suspense>
        
        <ExitIntentPopup />
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
